import classNames from 'classnames'
import { ReactElement, useState } from 'react'
import Spinner from 'app/components/Spinner'
import { EDITABLE_CELL_HOVER } from 'app/components/table/cellInteraction'

/** Unified empty-cell affordance classes: a quiet "+" chip at rest whose verb
 *  expands when the pointer enters the cell (tables provide the `group/cell`
 *  scope). Rest state stays calm, capability reveals on approach.
 *
 *  Deliberately carries no background tint. Most of these chips sit inside a
 *  wrapping trigger that already tints the whole cell, and two translucent
 *  layers composite to roughly double the alpha, which reads as a fourth level
 *  the three-level model does not have. Whichever element is the actual click
 *  target owns the tint: add `EDITABLE_CELL_HOVER` when this chip is the target
 *  itself rather than sitting inside one. */
export const inlineAddChipClasses = `inline-flex h-5 cursor-pointer items-center whitespace-nowrap rounded border-[0.5px] border-default px-1.5 text-label-xs text-minimal transition-colors hover:border-(--fg-minimal) hover:text-default group-hover/cell:border-strong group-hover/cell:text-subtle`

/** How small the chip may get: the resting "+" button, a square matching the
 *  chip's `h-5`. The verb truncates down to this and no further, so a narrow
 *  column never squeezes the affordance itself. */
const CHIP_FLOOR = `min-w-5`

/** Presentational "+ Verb" chip for empty actionable cells whose click handling
 *  lives on a wrapping trigger (combos, dropdown menus).
 *
 *  The expanded verb is wider than some columns, so the chip has to shrink, but
 *  it must not squeeze the affordance itself. `CHIP_FLOOR` stops it at the size
 *  of the resting "+" button, a square matching the chip's own `h-5`, and the
 *  verb (`min-w-0 truncate`) is the only part that gives way. Dropping the floor
 *  to `min-w-0` lets the chip shrink below its own padding and clip the "+";
 *  omitting it entirely stops the chip shrinking at all, so the verb overflows
 *  the cell. Same construction as InlineAddActionLink below. */
export function InlineAddChip({ verb, className }: { verb: string; className?: string }): ReactElement {
  return (
    // `data-slot` lets a host relax its own `min-width: auto` for this chip only
    // (see ComboButtonView's icon slot) without letting real icons squash.
    <span data-slot="inline-add-chip" className={classNames(inlineAddChipClasses, CHIP_FLOOR, className)}>
      <span className="shrink-0">+</span>
      <span className="hidden min-w-0 truncate group-hover/cell:inline">&nbsp;{verb}</span>
    </span>
  )
}

type InlineAddActionLinkProps = {
  label: string
  onClick: (e: React.MouseEvent<HTMLSpanElement>) => void | Promise<void>
}

/** Self-contained clickable variant of the empty-cell affordance. It is its own
 *  click target with no tinting wrapper above it, so it owns the tint. */
export function InlineAddActionLink({ label, onClick }: InlineAddActionLinkProps): ReactElement {
  const [loading, setLoading] = useState<boolean>(false)
  const verb = label.replace(/^\+\s*/, ``)

  return (
    // `overflow-hidden` keeps the expanded verb inside the cell: a chip wider
    // than its column would otherwise paint over the cell border and the
    // neighbouring cell, which its transparent background shows straight through.
    <div className="flex min-w-0 items-center overflow-hidden px-2">
      <span
        aria-label={verb}
        title={verb}
        className={classNames(inlineAddChipClasses, EDITABLE_CELL_HOVER, CHIP_FLOOR)}
        onClick={async (e) => {
          e.stopPropagation()
          e.preventDefault()
          setLoading(true)
          try {
            await onClick(e)
          } finally {
            setLoading(false)
          }
        }}
      >
        {loading ? (
          <span className="mx-auto inline-flex items-center">
            <Spinner size={4} />
          </span>
        ) : (
          <>
            <span className="shrink-0">+</span>
            <span className="hidden min-w-0 truncate group-hover/cell:inline">&nbsp;{verb}</span>
          </>
        )}
      </span>
    </div>
  )
}
